// https://nathanbirrell.me/notes/composition-mobx-state-tree/
import { types, flow } from 'mobx-state-tree';
import axios, { AxiosRequestConfig } from 'axios';

import { getCompanyDomain } from '@src/helpers';
import request from 'src/services/request';
import { toast } from 'src/components';

const CANCELED_OPERATION_MESSAGE = 'Operation canceled by the user.';

const Model = {
  state: types.maybe(types.enumeration('state', ['init', 'pending', 'error', 'done'])),
  error: types.maybe(types.frozen()),
  countPendingCalls: 0
};

const Views = (self: any) => ({
  get hasError() {
    return self.state === 'error';
  },
  get isFetching() {
    return Boolean(self.countPendingCalls);
  }
  // ... and other shared views here, for example, hasData() might check if there are items in a self.data array
});

function replaceAll(str, find, replace) {
  return str.replace(new RegExp(escapeRegExp(find), 'g'), replace);
}

function escapeRegExp(str) {
  return str;
}

type Options = {
  axiosConfig?: AxiosRequestConfig;
  onFetchSuccess?: (response: any, method: 'post' | 'fetch' | 'delete') => any;
  onFetchError?: (error: any, method: 'post' | 'fetch' | 'delete') => any;
};

function generateAction({ self, requestFn, method }) {
  return flow(function*<Parameters>(endpoint: string, parameters?: Parameters, options?: Options) {
    const { axiosConfig, onFetchSuccess, onFetchError } = options || {};
    self.state = 'pending';
    self.countPendingCalls += 1;

    try {
      const response = yield requestFn(endpoint, parameters, axiosConfig);

      self.error = null;
      if (typeof onFetchSuccess === 'function') {
        onFetchSuccess(response, method); // callback to be overriden in composed model
      }
      self.state = 'done';

      return response;
    } catch (error) {
      self.error = error;
      self.state = 'error';
      if (typeof self.onFetchError === 'function' || typeof onFetchError === 'function') {
        onFetchError?.(error, method);
        self.onFetchError(error); // callback to be overriden in composed model
      }
      return error;
    } finally {
      self.countPendingCalls -= 1;
    }
  });
}

const Actions = (self: any) => {
  const CancelToken = axios.CancelToken;
  let source = CancelToken.source();
  const companyId = getCompanyDomain();

  return {
    fetch: generateAction({
      self,
      requestFn: (endpoint: string, _, axiosConfig?: AxiosRequestConfig) =>
        request.get(replaceAll(endpoint, 'companyId', companyId), {
          ...axiosConfig,
          cancelToken: source.token
        }),
      method: 'get'
    }),
    post: generateAction({
      self,
      requestFn: (endpoint: string, parameters, axiosConfig?: AxiosRequestConfig) =>
        request.post(replaceAll(endpoint, 'companyId', companyId), parameters, axiosConfig),
      method: 'post'
    }),
    patch: generateAction({
      self,
      requestFn: (endpoint: string, parameters, axiosConfig?: AxiosRequestConfig) =>
        request.put(replaceAll(endpoint, 'companyId', companyId), parameters, axiosConfig),
      method: 'patch'
    }),
    delete: generateAction({
      self,
      requestFn: (endpoint: string, _, axiosConfig?: AxiosRequestConfig) =>
        request.delete(replaceAll(endpoint, 'companyId', companyId), {
          ...axiosConfig
        }),
      method: 'delete'
    }),
    onFetchError: function (error) {
      //  errors now hanled in request.ts files

    },

    abort: function () {
      source.cancel(CANCELED_OPERATION_MESSAGE);
      source = CancelToken.source();
    }

    // If you like, you could create the callbacks `onFetchSuccess` and `onFetchError` with a simple error log to remind you to define these in your Products and Posts models
  };
};

export const ApiStore = types
  .model('ApiStore', Model)
  .views(Views)
  .actions(Actions);
